<template>
  <div class="nodo-componente" :class="claseNivel">
    <!-- Cabecera del componente -->
    <div
      class="nodo-cabecera"
      :class="{ 'nodo-error': !!mensajeError, 'nodo-padre': tieneHijos, 'nodo-agrupado': !!grupoColor }"
      :style="grupoColor ? { borderLeftColor: grupoColor.borde, borderLeftWidth: '5px' } : {}"
    >
      <div class="nodo-info">
        <div class="nodo-encabezado">
          <strong class="nodo-numeracion">{{ obtenerNombreNivel(nivel) }} {{ numeracionJerarquica }}</strong>
          <strong class="nodo-nombre">{{ componente.nombre }}</strong>
          <span
            v-if="componente.grupo_variante && grupoColor"
            class="grupo-chip"
            :style="{ backgroundColor: grupoColor.bg, color: grupoColor.texto, borderColor: grupoColor.borde }"
            title="Aspectos con este mismo grupo comparten un único slot de peso"
          >⧉ {{ componente.grupo_variante }}</span>
        </div>
        <span class="nodo-descripcion">{{ componente.descripcion || '—' }}</span>
      </div>
      <div class="nodo-peso">
        <span v-if="porcentajeTexto != null" class="peso-porcentaje">{{ porcentajeTexto }}</span>
        <div class="peso-input-row">
          <span class="peso-label">Peso:</span>
          <input
            type="text"
            inputmode="decimal"
            :value="valorMostrado"
            class="input-personalizado"
            @focus="onFocus"
            @keydown="onKeydown"
            @input="onPesoInput"
            @blur="onBlur"
            :disabled="loading"
          />
        </div>
      </div>
    </div>

    <!-- Hijos recursivos -->
    <div v-if="tieneHijos" class="nodo-hijos">
      <NodoComponente
        v-for="hijo in hijosOrdenados"
        :key="hijo.id_componente"
        :componente="hijo"
        :nivel="nivel + 1"
        :loading="loading"
        :lockedMap="lockedMap"
        :componentes="componentes"
        :storeRaizId="storeRaizId"
        :obtenerNumeracion="obtenerNumeracion"
        :mostrarHijos="mostrarHijos"
        :nivelMaximo="nivelMaximo"
        @limitar-decimales="$emit('limitar-decimales', $event)"
        @redistribuir="$emit('redistribuir', $event)"
        @formatear="$emit('formatear', $event)"
        @update-peso="$emit('update-peso', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Nodo RECURSIVO del árbol de estructura.
 *
 * Se invoca a sí mismo para pintar su descendencia (patrón Composite). El árbol
 * llega a 4 niveles, y la profundidad se comunica con color y sangría.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, inject, type Ref } from 'vue'
import type Componente from '@/interfaces/Componente'
import { sumaColapsada, colorDeGrupo } from '@/utils/pesosGrupo'

interface ComponenteAplanado {
  ref: Componente
  nivel: number
}

const props = defineProps<{
  componente: Componente
  nivel: number
  loading: boolean
  lockedMap: Map<number, boolean>
  componentes: ComponenteAplanado[]
  storeRaizId: number | null
  obtenerNumeracion?: (comp: Componente, nivel: number) => string
  mostrarHijos?: boolean
  nivelMaximo?: number
}>()

const emit = defineEmits<{
  (e: 'limitar-decimales', comp: Componente): void
  (e: 'redistribuir', aplanado: ComponenteAplanado): void
  (e: 'formatear', comp: Componente): void
  (e: 'update-peso', payload: { comp: Componente; valor: number }): void
}>()

const tieneHijos = computed(() =>
  (props.mostrarHijos !== false) &&
  (props.nivelMaximo === undefined || props.nivel < props.nivelMaximo) &&
  ((props.componente.children?.length ?? 0) > 0)
)

const hijosOrdenados = computed(() =>
  [...(props.componente.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

const claseNivel = computed(() => `nodo-nivel-${Math.min(props.nivel, 5)}`)

// Color determinístico del grupo de peso compartido (diferenciador visual).
const grupoColor = computed(() =>
  props.componente.grupo_variante ? colorDeGrupo(props.componente.grupo_variante) : null
)

const aplanado = computed<ComponenteAplanado>(() => ({
  ref: props.componente,
  nivel: props.nivel
}))

const numeracionJerarquica = computed(() => {
  if (props.obtenerNumeracion) {
    return props.obtenerNumeracion(props.componente, props.nivel)
  }
  return props.componente.ordinal ?? ''
})

// Inyectado desde la vista para obtener el nombre del nivel
const obtenerNombreNivel = inject<(nivel: number) => string>('obtenerNombreNivel', () => '')
const decimales = inject<Ref<number>>('decimales')

const mensajeError = computed(() => {
  const hijos = props.componente.children ?? []
  if (!hijos.length) return null
  const dec = decimales?.value ?? 2
  // Suma colapsando grupos de peso compartido (variantes cuentan una vez).
  const suma = sumaColapsada(hijos)
  const sumaRedondeada = parseFloat(suma.toFixed(dec))
  const totalEsperado = props.componente.padre_id == null ? 100 : (props.componente.peso ?? 0)
  const diferencia = Math.abs(sumaRedondeada - totalEsperado)
  if (diferencia > 0.5) {
    return `La suma de los hijos es ${sumaRedondeada}, debe ser ${totalEsperado}.`
  }
  return null
})

const editando = ref(false)

const valorMostrado = computed(() => {
  if (editando.value || props.componente.peso == null) return props.componente.peso ?? 0
  const dec = decimales?.value ?? 2
  return Number(props.componente.peso.toFixed(dec))
})

const porcentajeTexto = computed(() => {
  const peso = props.componente.peso
  if (peso == null) return null

  const padreId = props.componente.padre_id
  const esNivel1 = padreId == null || padreId === props.storeRaizId
  let totalPadre: number

  if (esNivel1) {
    totalPadre = 100
  } else {
    const padre = props.componentes.find(c => c.ref.id_componente === padreId)?.ref
    if (!padre || !padre.peso) return null
    totalPadre = padre.peso
  }

  const pct = (peso / totalPadre) * 100
  const redondeado = parseFloat(pct.toFixed(1))

  if (!esNivel1) {
    const nombreNivelPadre = obtenerNombreNivel(props.nivel - 1)
    const num = numeracionJerarquica.value
    const numPadre = num.split('.').slice(0, -1).join('.')
    if (nombreNivelPadre && numPadre) {
      return `${redondeado}% ${nombreNivelPadre} ${numPadre}`
    }
  }

  return `${redondeado}%`
})

let pesoAlEnfocar: number | null = null

function onFocus() {
  editando.value = true
  pesoAlEnfocar = props.componente.peso ?? null
}

function onKeydown(event: KeyboardEvent) {
  if (event.ctrlKey || event.metaKey) return
  const permitidas = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End']
  if (permitidas.includes(event.key)) return
  if (event.key === ',' || event.key === '.') return
  if (event.key >= '0' && event.key <= '9') return
  event.preventDefault()
}

function onPesoInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value.replace(',', '.')
  const valor = parseFloat(raw) || 0
  emit('update-peso', { comp: props.componente, valor })
  emit('limitar-decimales', props.componente)
}

function onBlur() {
  editando.value = false
  emit('formatear', props.componente)
  if (props.componente.peso !== pesoAlEnfocar) {
    emit('redistribuir', aplanado.value)
  }
  pesoAlEnfocar = null
}
</script>

<style scoped>
.nodo-componente {
  border-radius: 8px;
  padding: 0;
  margin-bottom: 6px;
}

.nodo-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 8px;
  gap: 1rem;
  flex-wrap: wrap;
  transition: box-shadow 0.2s ease;
}

.nodo-cabecera.nodo-padre {
  font-size: 1rem;
}

.nodo-cabecera.nodo-error {
  outline: 2px solid #dc3545;
}

.nodo-info {
  flex: 1;
  min-width: 200px;
}

.nodo-encabezado {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 4px;
}

.nodo-numeracion {
  font-size: 0.85rem;
  color: #0e69a0;
  font-weight: 700;
  white-space: nowrap;
  min-width: 120px;
}

.nodo-nombre {
  display: inline;
  font-size: 0.95rem;
  color: #1a1a1a;
  font-weight: 600;
}

.nodo-descripcion {
  display: block;
  font-size: 0.85rem;
  color: #666;
  margin-top: 2px;
}

.grupo-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 9px;
  border: 1px solid;
  border-radius: 11px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

/* Refuerza la asociación visual de los aspectos agrupados. */
.nodo-cabecera.nodo-agrupado {
  border-left-style: solid;
}

.nodo-peso {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  flex-shrink: 0;
}

.peso-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.peso-label {
  color: #444;
  font-weight: 600;
  font-size: 0.9rem;
}

.peso-porcentaje {
  font-size: 0.8rem;
  color: #0e69a0;
  font-weight: 600;
  white-space: nowrap;
  text-align: right;
  opacity: 0.85;
}

.input-personalizado {
  width: 100px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  background-color: #f9f9f9;
  transition: background-color 0.2s ease;
}

.input-personalizado:disabled {
  background-color: #e8e8e8;
  color: #666;
  cursor: not-allowed;
}

.btn-lock.locked .icono-lock {
  fill: none;
  stroke: #d32f2f;
  color: #d32f2f;
}

.btn-lock:hover .icono-unlock {
  stroke: #444;
  color: #444;
}

.btn-lock.locked:hover .icono-lock {
  stroke: #b71c1c;
  color: #b71c1c;
}

.nodo-hijos {
  margin-left: 20px;
  padding-left: 12px;
  border-left: 3px solid #ccd6e0;
  margin-top: 4px;
}

/* Nivel 1: azul oscuro */
.nodo-nivel-1 > .nodo-cabecera {
  background-color: #d0e2f2;
  border-left: 5px solid #0e69a0;
  box-shadow: 0 3px 8px rgba(0,0,0,0.15);
}
.nodo-nivel-1 > .nodo-cabecera .nodo-nombre {
  font-size: 1rem;
}

/* Nivel 2: azul medio */
.nodo-nivel-2 > .nodo-cabecera {
  background-color: #e1ecf5;
  border-left: 4px solid #5a9fc9;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}
.nodo-nivel-2 > .nodo-cabecera .nodo-nombre {
  font-size: 0.95rem;
}

/* Nivel 3: azul claro */
.nodo-nivel-3 > .nodo-cabecera {
  background-color: #eaf2f9;
  border-left: 3px solid #8bb8d6;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.nodo-nivel-3 > .nodo-cabecera .nodo-nombre {
  font-size: 0.93rem;
}

/* Nivel 4: gris azulado */
.nodo-nivel-4 > .nodo-cabecera {
  background-color: #f0f4f8;
  border-left: 3px solid #a8c5d8;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.nodo-nivel-4 > .nodo-cabecera .nodo-nombre {
  font-size: 0.9rem;
}

/* Nivel 5+: muy claro */
.nodo-nivel-5 > .nodo-cabecera {
  background-color: #f6f8fb;
  border-left: 2px solid #c0d4e3;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.nodo-nivel-5 > .nodo-cabecera .nodo-nombre {
  font-size: 0.88rem;
}
</style>
